import React from 'react';
import PropTypes from 'prop-types';
import AssignButton from 'shared/LessonOverview/Cards/AssignButton';
import useFlags from '@/hooks/useFlags';
import CloneButton from './CloneButton';
import actionBarStyles from './styles';
import {
  DeleteButton,
  EditNewSlideShowButton,
  EditExistingSlideShowButton,
  PresentButton,
  PrintButton,
} from './Buttons';
import { SLIDE_SHOW_MODEL_TYPES } from 'shared/slide_shows/utils';
import { toCamelCase } from 'modules/TCIUtils';

// pacing info for only modules
const Pacing = ({ slideShow }) => {
  const { accessibilityReflow } = useFlags();
  const { pacing } = actionBarStyles({ reflow: accessibilityReflow });

  if (!slideShow.pacing) return null;

  return (
    <div className={pacing()}>
      <i className="fa fa-clock-o" /> {slideShow.pacing} mins
    </div>
  );
};

const ActionBar = ({
  hideTabs, slideShow, isStaffer, stafferId, teacherCard,
}) => {
  const { accessibilityReflow } = useFlags();
  const { actions, bar } = actionBarStyles({ reflow: accessibilityReflow });

  const showStafferActions = () => {
    if (!isStaffer) return null;

    let initialValues;

    if (slideShow.assignable[0]?.slideGroups?.length) {
      initialValues = {
        modelType: SLIDE_SHOW_MODEL_TYPES[slideShow.activityType],
        modelsToAssign: slideShow.assignable[0].slideGroups,
        slideShowSelect: slideShow.assignable[0].value,
      };
    }
    else {
      initialValues = {
        modelType: SLIDE_SHOW_MODEL_TYPES[slideShow.activityType],
        modelsToAssign: slideShow.assignable[0].value,
      };
    }

    return (
      <React.Fragment>
        {Object.keys(SLIDE_SHOW_MODEL_TYPES).includes(slideShow.activityType) && slideShow.assignable && (
          <AssignButton
            className="tw-w-7 tw-h-7 tw-p-1.25 tw-flex tw-justify-center tw-items-center"
            initialValues={initialValues}
            isAssigned={slideShow.isAssigned}
            lessonId={parseInt(slideShow.lesson.id, 10)}
            program={toCamelCase(slideShow.program)}
            stafferId={stafferId}
          />
        )}

        {teacherCard && (
          <EditExistingSlideShowButton slideShow={slideShow} />
        )}
        {!teacherCard && (
          <EditNewSlideShowButton
            hideTabs={hideTabs}
            slideShow={slideShow}
            useNewBuilder={slideShow.useNewBuilder}
          />
        )}
        <CloneButton
          hideTabs={hideTabs}
          id={slideShow.id}
          slideShowId={slideShow.slideShowId}
          title={slideShow.title}
          type={slideShow.className}
          useNewBuilder={slideShow.useNewBuilder}
        />
        {slideShow.activityType !== 'video_activity' && <PrintButton slideShow={slideShow} />}
        <DeleteButton slideShow={slideShow} />
      </React.Fragment>
    );
  };

  return (
    <div className={bar()}>
      <div className={actions()}>
        <PresentButton slideShow={slideShow} />
        {showStafferActions()}
      </div>
      <Pacing slideShow={slideShow} />
    </div>
  );
};

const slideShowPropTypes = PropTypes.shape({
  activityType: PropTypes.string,
  assignable: PropTypes.object,
  className: PropTypes.string,
  id: PropTypes.number.isRequired,
  isAssigned: PropTypes.bool.isRequired,
  lesson: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  links: PropTypes.shape({
    deletePath: PropTypes.string,
    editPath: PropTypes.string,
    presentPath: PropTypes.string,
    printUrl: PropTypes.string,
    sharePath: PropTypes.string,
    unsharePath: PropTypes.string
  }).isRequired,
  pacing: PropTypes.number,
  program: PropTypes.object,
  shareable: PropTypes.bool,
  shared: PropTypes.bool,
  slideShowId: PropTypes.number,
  stafferId: PropTypes.number,
  title: PropTypes.string,
  useNewBuilder: PropTypes.bool,
}).isRequired;

ActionBar.propTypes = {
  hideTabs: PropTypes.bool,
  isStaffer: PropTypes.bool,
  slideShow: slideShowPropTypes,
  stafferId: PropTypes.number.isRequired,
  teacherCard: PropTypes.bool,
};

ActionBar.defaultProps = {
  completeLessonGuideId: null,
  hideTabs: false,
  isStaffer: true,
  teacherCard: true,
};

Pacing.propTypes = {
  slideShow: slideShowPropTypes
};

export default ActionBar;
